import { useMemo } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import type { RollsetListItem } from "../models/RollshopOperations";
import {
	ASSIGNMENT_STATUS_LABEL,
	formatDiameter,
	getAssignmentStatus,
	getRollsetPositions,
	OP_STATUS_IN_USE,
	OP_STATUS_PREPARED_FOR_LINE,
	type PassStatusOption,
	type RollsetStatusChange,
	type RollsProductionRow,
} from "../models/Rollshop";
import { PASS_CONDITION_HEIGHT, PassConditionGrid } from "./PassConditionGrid";
import { SetOpStatusButton } from "./SetOpStatusButton";
import {
	OPERATIONAL_WRITES_LOCKED_MESSAGE,
	useOperationalWritesLocked,
} from "@/shared/hooks/useOperationalWritesLocked";

interface InfoField {
	label: string;
	value: string | null | undefined;
	numeric?: boolean;
}

// Legacy rollset info: label and value pairs, two per row.
const InfoGrid = ({ rows }: { rows: [InfoField, InfoField][] }) => (
	<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 3 }}>
		{rows.flat().map((field, index) => (
			<Box
				key={field.label}
				sx={{
					display: "flex",
					alignItems: "baseline",
					gap: 1,
					minWidth: 0,
					py: 0.6,
					borderTop: index < 2 ? 0 : "1px solid",
					borderColor: "divider",
				}}
			>
				<Typography
					noWrap
					sx={{
						fontSize: "0.8rem",
						color: "text.secondary",
						flex: "0 0 112px",
					}}
				>
					{field.label}
				</Typography>
				<Typography
					noWrap
					title={field.value ?? undefined}
					sx={{
						fontSize: "0.875rem",
						fontWeight: 600,
						fontVariantNumeric: field.numeric ? "tabular-nums" : undefined,
					}}
				>
					{field.value || "-"}
				</Typography>
			</Box>
		))}
	</Box>
);

interface Props {
	rollset: RollsetListItem | null;
	production: RollsProductionRow[];
	isLoadingProduction: boolean;
	passStatus: PassStatusOption[];
	statusChanges: RollsetStatusChange[];
	isSettingStatus: boolean;
	onTurn: () => void;
	onSetStatus: (statusCode: string) => void;
	onEditPass: (row: RollsProductionRow) => void;
	onClose: () => void;
	onStatusMenuOpenChange: (open: boolean) => void;
}

export const RollshopDetailPanel = ({
	rollset,
	production,
	isLoadingProduction,
	passStatus,
	statusChanges,
	isSettingStatus,
	onTurn,
	onSetStatus,
	onEditPass,
	onClose,
	onStatusMenuOpenChange,
}: Props) => {
	const writesLocked = useOperationalWritesLocked();
	const lockedReason = writesLocked ? OPERATIONAL_WRITES_LOCKED_MESSAGE : "";
	// Legacy: Prepared for line and In use are only set from the mill setup flows.
	const targets = useMemo(
		() =>
			statusChanges.filter(
				(c) =>
					c.FromCode === rollset?.StatusCode &&
					c.ToCode !== OP_STATUS_IN_USE &&
					c.ToCode !== OP_STATUS_PREPARED_FOR_LINE,
			),
		[statusChanges, rollset?.StatusCode],
	);

	if (!rollset) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography color="text.secondary">
					Select a rollset to see its rolls and pass condition.
				</Typography>
			</Box>
		);
	}

	const positions = getRollsetPositions(rollset);
	const assignment = getAssignmentStatus(rollset);
	const isAssigned = assignment === "ASSIGNED";
	const isInUse = rollset.StatusCode === OP_STATUS_IN_USE;
	const isPrepared = rollset.StatusCode === OP_STATUS_PREPARED_FOR_LINE;

	// Common stands pair Top and Bottom per row; a straightener lists each roll with its diameter.
	const noRoll = "None";
	const rollRows: [InfoField, InfoField][] =
		positions.length === 2
			? [
					[
						{ label: "Top Roll", value: positions[0].rollName ?? noRoll },
						{ label: "Bottom Roll", value: positions[1].rollName ?? noRoll },
					],
					[
						{
							label: "Top Diameter",
							value: formatDiameter(positions[0].diameter),
							numeric: true,
						},
						{
							label: "Bottom Diameter",
							value: formatDiameter(positions[1].diameter),
							numeric: true,
						},
					],
				]
			: positions.map((p) => [
					{ label: `Roll ${p.label}`, value: p.rollName ?? noRoll },
					{
						label: `Roll ${p.label} Diameter`,
						value: formatDiameter(p.diameter),
						numeric: true,
					},
				]);

	const turnBlockedReason = writesLocked
		? lockedReason
		: !isAssigned
			? "The rollset must have all its rolls assigned."
			: isInUse
				? "The rollset is In use."
				: "";
	const statusBlockedReason = writesLocked
		? lockedReason
		: !isAssigned
			? "The rollset must have all its rolls assigned."
			: isInUse || isPrepared
				? `The rollset is ${rollset.StatusName}. Its Op. Status changes from Mill Setup or Rolls Cartridge.`
				: targets.length === 0
					? `There is no manual Op. Status change from ${rollset.StatusName}.`
					: "";

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<DetailHeader
				title={rollset.Name}
				onClose={onClose}
				isSaving={isSettingStatus}
				actions={
					<>
						<SetOpStatusButton
							targets={targets}
							disabled={statusBlockedReason !== ""}
							disabledReason={statusBlockedReason}
							isSaving={isSettingStatus}
							onConfirm={onSetStatus}
							onOpenChange={onStatusMenuOpenChange}
						/>
						<Tooltip title={turnBlockedReason}>
							<span>
								<Button
									size="small"
									variant="contained"
									disabled={turnBlockedReason !== ""}
									onClick={onTurn}
								>
									Turn
								</Button>
							</span>
						</Tooltip>
					</>
				}
			/>
			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<DetailSection title="Rollset" icon={<InfoOutlinedIcon />}>
						<InfoGrid
							rows={[
								[
									{ label: "Stand", value: rollset.StandName },
									{ label: "Cartridge", value: rollset.CartridgeName },
								],
								...rollRows,
								[
									{
										label: "Status",
										value: ASSIGNMENT_STATUS_LABEL[assignment],
									},
									{ label: "Op. Status", value: rollset.StatusName },
								],
							]}
						/>
					</DetailSection>
					<Box
						sx={{
							flex: "0 1 auto",
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
						}}
					>
						<DetailSection
							title="Pass Condition"
							icon={<FormatListNumberedIcon />}
							fill
						>
							<Box
								sx={{
									flex: "0 1 auto",
									height: PASS_CONDITION_HEIGHT,
									minHeight: 0,
								}}
							>
								{!isAssigned ? (
									<Typography color="text.secondary" sx={{ p: 1 }}>
										The rollset must have all its rolls assigned.
									</Typography>
								) : isLoadingProduction && production.length === 0 ? (
									<CenteredLazyLoading />
								) : (
									<PassConditionGrid
										rows={production}
										passStatus={passStatus}
										editable={!isInUse}
										lockedReason={lockedReason}
										onEdit={onEditPass}
									/>
								)}
							</Box>
						</DetailSection>
					</Box>
				</Box>
			</DetailBody>
		</Box>
	);
};
